import React, { memo, useMemo } from 'react';
import { Typography, TypographyProps } from '@mui/material';
import clsx from 'clsx';
import style from './index.module.scss';
import { useTranslate } from 'hooks/use-translate';
import { useSourceEmployees } from '../../hooks';

type Employee = ReturnType<typeof useSourceEmployees>['map'][string];

interface LabelProps extends TypographyProps {
  employee: Employee | null;
  loading: boolean;
}
/**
 * The presentational half: the caller has the employee already (F2-06: the clinical
 * meetings body resolves employees once for its rows instead of a source
 * subscription per row). `TemplateEmployeeSource` below keeps the hook for the grids.
 */
export const TemplateEmployeeLabel = memo<LabelProps>(
  ({ employee, loading, className, ...rest }) => {
    const { t } = useTranslate();

    const label = useMemo(() => {
      if (loading) {
        return t('loading');
      }

      if (!employee) {
        return '';
      }

      return [employee.title, employee.isActive ? null : `[${t('inactive')}]`]
        .filter(Boolean)
        .join(' ');
    }, [employee, t, loading]);

    return (
      <Typography className={clsx(style.root, className)} {...rest}>
        {label}
      </Typography>
    );
  },
);

interface Props extends TypographyProps {
  userEmployeeProfileID: string | null | undefined;
}
export const TemplateEmployeeSource = memo<Props>(
  ({ userEmployeeProfileID, className, ...rest }) => {
    const { t } = useTranslate();
    const { loading, map } = useSourceEmployees(false);

    const employee = userEmployeeProfileID ? map[userEmployeeProfileID] : null;

    const label = useMemo(() => {
      if (loading) {
        return t('loading');
      }

      if (!employee) return '';

      return [employee.title, employee.isActive ? null : `[${t('inactive')}]`]
        .filter(Boolean)
        .join(' ');
    }, [employee, t, loading]);

    return (
      <Typography className={clsx(style.root, className)} {...rest}>
        {label}
      </Typography>
    );
  },
);
